import {
    GetOrdersSortOptionType,
    OrdersSortDirection,
    type AdminOrderListItem,
    type GetOrdersQuery,
} from "@models/order";

// Ruling O5 (2026-10-27): every Orders column is sortable. Each column's id
// (its accessorKey) and the GET /orders sortBy it asks for.
export const ORDER_SORT_BY = {
    orderNumber: GetOrdersSortOptionType.OrderNumber,
    organizationName: GetOrdersSortOptionType.OrganizationName,
    locationName: GetOrdersSortOptionType.LocationName,
    status: GetOrdersSortOptionType.Status,
    total: GetOrdersSortOptionType.Total,
    createdAt: GetOrdersSortOptionType.CreatedAt,
    lastUpdatedAt: GetOrdersSortOptionType.LastUpdatedAt,
} satisfies Partial<Record<keyof AdminOrderListItem, GetOrdersSortOptionType>>;

export type OrderSortColumn = keyof typeof ORDER_SORT_BY;

export interface OrderSort {
    columnId: OrderSortColumn;
    desc: boolean;
}

// The list's long-standing order (DEFAULT_ORDERS_QUERY): newest first.
const DEFAULT_ORDER_SORT: OrderSort = { columnId: "createdAt", desc: true };

const isOrderSortColumn = (value: string): value is OrderSortColumn =>
    Object.keys(ORDER_SORT_BY).includes(value);

/**
 * The sort `?sortBy=<column id>&sortDirection=asc|desc` names. Anything else
 * (no sort, an unknown column or direction) is the default, newest first.
 */
export const parseOrderSort = (
    sortBy: string | null,
    sortDirection: string | null,
): OrderSort =>
    sortBy !== null &&
    isOrderSortColumn(sortBy) &&
    (sortDirection === "asc" || sortDirection === "desc")
        ? { columnId: sortBy, desc: sortDirection === "desc" }
        : DEFAULT_ORDER_SORT;

/** GET /orders' sortBy and sortDirection for a sort. */
export const toOrdersQuerySort = (
    sort: OrderSort,
): Pick<GetOrdersQuery, "sortBy" | "sortDirection"> => ({
    sortBy: [ORDER_SORT_BY[sort.columnId]],
    sortDirection: sort.desc ? OrdersSortDirection.Desc : OrdersSortDirection.Asc,
});
